import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';

import { LOCK } from '../icons';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import UserChip from '../users/UserChip';
import { getCollectionManagers } from '../../services/sharing';
import { apiErrorMessage } from '../../utils/apiErrors';
import { GRANT_READ_ONLY_REASON } from '../../utils/elementAccess';

// The words under an element a group built from records reads (owner ruling
// b79, and the owner's note on it: "I want users to have a clear path to who
// can help them if they hit a permission wall"). One sentence, the same for
// every reason the API gives (b76 the account link, b78 a field the group's
// rule reads, b79 a field it reads through this record): naming the group or
// its rule would tell a writer what only managers see.
export const LOCK_NOTICE_BEFORE = 'This element controls user permissions, so you must be a ';
export const LOCK_NOTICE_LINK = 'collection manager';
export const LOCK_NOTICE_AFTER = ' to change it.';

/**
 * Who can help: the collection's managers (GET …/managers — name and avatar,
 * never email), read when the modal opens and not before.
 */
export function CollectionManagersModal({ open, collectionId, onClose }) {
	const [state, setState] = useState({ loaded: false, loading: false, managers: [], error: null });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!open || !collectionId) return undefined;
		let live = true;
		setState({ loaded: false, loading: true, managers: [], error: null });
		getCollectionManagers(collectionId)
			.then(managers => { if (live) setState({ loaded: true, loading: false, managers, error: null }); })
			.catch(error => {
				if (live) setState({ loaded: false, loading: false, managers: [], error: apiErrorMessage(error, 'Could not load the collection managers.') });
			});
		return () => { live = false; };
	}, [open, collectionId, attempt]);

	return (
		<Modal
			open={open}
			title="Collection managers"
			onCancel={onClose}
			footer={<Button onClick={onClose}>Close</Button>}
			destroyOnHidden
		>
			<p className="form-panel__hint">Ask one of them to make this change.</p>
			<DialogLoadState
				loaded={state.loaded}
				loading={state.loading}
				error={Boolean(state.error)}
				errorDetail={state.error || undefined}
				isEmpty={!state.managers.length}
				empty={<p className="form-panel__hint">No managers to show.</p>}
				label="the collection managers"
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={120}
			>
				<ul className="collection-managers" aria-label="Collection managers">
					{state.managers.map(user => (
						<li key={user._id} className="collection-managers__row">
							<UserChip user={user} interactive={false} />
						</li>
					))}
				</ul>
			</DialogLoadState>
		</Modal>
	);
}

/** The notice itself: a hint under the locked element, with the door to the managers. */
export default function AccessLockNotice({ collectionId }) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<p className="form-panel__hint access-lock-note">
				<LOCK className="access-lock-note__icon" aria-hidden="true" />
				<span>
					{LOCK_NOTICE_BEFORE}
					<Button type="link" size="small" className="access-lock-note__link" onClick={() => setOpen(true)}>
						{LOCK_NOTICE_LINK}
					</Button>
					{LOCK_NOTICE_AFTER}
				</span>
			</p>
			{open ? <CollectionManagersModal open={open} collectionId={collectionId} onClose={() => setOpen(false)} /> : null}
		</>
	);
}

/**
 * The notice under an element an element rule holds this person to "see
 * only" (KPR-509; keepr-api § Element grants): the plan's one sentence, and
 * the same door to who can help as the lock above. It never says WHICH rule
 * or who else may change it — the rule is the managers' to read.
 */
export function GrantReadOnlyNotice({ collectionId }) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<p className="form-panel__hint access-lock-note access-lock-note--grant">
				<LOCK className="access-lock-note__icon" aria-hidden="true" />
				<span>
					{GRANT_READ_ONLY_REASON}{' '}
					{collectionId ? (
						<Button type="link" size="small" className="access-lock-note__link" onClick={() => setOpen(true)}>
							Ask a collection manager
						</Button>
					) : null}
				</span>
			</p>
			{open ? <CollectionManagersModal open={open} collectionId={collectionId} onClose={() => setOpen(false)} /> : null}
		</>
	);
}
